/* eslint-disable @typescript-eslint/no-shadow */

import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useUniqueNativeID } from '../../../hooks';
import { PickerContainer, PickerControl } from '../Picker/subcomponents';
import { usePickerState } from '../Picker/Picker.hooks';
import type { PickersGroupProps } from '../Picker/Picker.types';
import {
  extractAllGroupsOptions,
  getOptionGroupsLabels,
  getSelectedValueParentGroup,
} from './PickersGroup.utils';

// ────────────────────────────────────────────────────────────────────────────────

export const PickersGroup = (props: PickersGroupProps) => {
  const {
    label,
    modalTitle,
    submitBtnLabel,
    value,
    onSubmit,
    optionsGroups,
    variation,
  } = props;
  const [valueParentLabel] = getSelectedValueParentGroup(optionsGroups, value);

  const labelNativeID = useUniqueNativeID('picker-group-label');
  const allAvailableOptions = useMemo(
    () => extractAllGroupsOptions(optionsGroups),
    [optionsGroups],
  );

  // ────────────────────────────────────────────────────────────────────

  const {
    selectedOptionValue,
    selectedOptionLabel,
    setSelectedOptionValue,
    handleSubmit,
    resetSelectedOptionValue,
  } = usePickerState({ value, options: allAvailableOptions, onSubmit });

  const groupsLabels = useMemo(
    () => getOptionGroupsLabels(optionsGroups),
    [optionsGroups],
  );
  const [selectedGroupLabel = ''] = useMemo(
    () => getSelectedValueParentGroup(optionsGroups, selectedOptionValue),
    [optionsGroups, selectedOptionValue],
  );
  const selectedGroupOptions = optionsGroups[selectedGroupLabel];

  // ─── HELPERS ────────────────────────────────────────────────────────

  const handleGroupChange = useCallback(
    (label) => {
      const groupFirstOption = optionsGroups?.[label]?.[0] ?? { value: '' };

      setSelectedOptionValue(groupFirstOption.value);
    },
    [optionsGroups, setSelectedOptionValue],
  );

  // ────────────────────────────────────────────────────────────────────

  return (
    <PickerContainer
      label={label}
      modalTitle={modalTitle}
      selectedOptionLabel={
        selectedOptionLabel
          ? `${valueParentLabel}, ${selectedOptionLabel}`
          : valueParentLabel
      }
      submitBtnLabel={submitBtnLabel}
      labelNativeID={labelNativeID}
      variation={variation}
      onSubmitBtnPress={handleSubmit}
      onDismiss={resetSelectedOptionValue}
    >
      <View style={styles.wrapper}>
        <View style={styles.pickerControlWrapper}>
          <PickerControl
            value={selectedGroupLabel}
            onChange={handleGroupChange}
            options={groupsLabels}
            selectionBorderRadius="left"
            accessibilityDescribedBy={labelNativeID}
          />
        </View>

        <View style={styles.pickerControlWrapper}>
          <PickerControl
            key={selectedGroupLabel}
            value={selectedOptionValue}
            onChange={setSelectedOptionValue}
            options={selectedGroupOptions}
            selectionBorderRadius="right"
            accessibilityDescribedBy={labelNativeID}
          />
        </View>
      </View>
    </PickerContainer>
  );
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
  },
  pickerControlWrapper: {
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 0,
  },
});
